import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../config/api';
import AuthService from '../services/authService';
import { EDIT_ORIGIN } from '../utils/editOrigin';
import { mintOpId, reAddAsEdit, patchFromWire } from '../utils/tripActivityOps';
import { dayPatchFromWire } from '../utils/tripPlanOps';

// How often queued ops go out while someone edits (plan §4.6: ~500 ms — an
// op is small, so there is no reason to batch for seconds).
const FLUSH_MS = 500;
// An op the server failed on unexpectedly (reason 'error', not logged) is
// sent again under the same id this many times before it is given up.
const MAX_ERROR_TRIES = 3;
// Network down: back off up to this between attempts.
const MAX_BACKOFF_MS = 15000;
// Ops whose newer copy replaces an older unsent one (enqueue).
const COALESCING = new Set(['activity.set', 'day.set', 'trip.set', 'stay.set', 'transport.set']);
// A day's place fields (one notice, one Keep theirs).
const DAY_PLACE = new Set(['location', 'locations', 'latitude', 'longitude']);
// Undo waits this long for the changes still on their way (the newest is
// what it takes back).
const UNDO_DRAIN_MS = 6000;
// An undo request that hasn't answered in this long is given up (and tried
// once more, under the same id).
const UNDO_TIMEOUT_MS = 15000;

// Unsent edits are kept on the device (D6), per person and trip, until the
// server confirms them — a closed tab or a phone without signal loses
// nothing; they go when the planner is next open and online. One that waited
// longer than this is dropped with a notice: the server takes a new stop's
// minted id for two days only (tripActivityFields.clientActivityIdOf), and a
// days-old edit landing over everyone's since would surprise them.
const STORE_PREFIX = 'lyf.tripOps.v1';
// 44 hours, not 48: the server judges a stop's minted id by ITS clock, and
// this one may run slow.
export const OFFLINE_MAX_AGE_MS = 44 * 60 * 60 * 1000;
const storeKey = (userId, tripId) => `${STORE_PREFIX}.${userId}.${tripId}`;
// Each open planner owns the entries it stored and says it is alive; another
// tab adopts (or ages out) only a dead tab's entries.
const TAB_KEY_PREFIX = `${STORE_PREFIX}.tab.`;
const TAB_HEARTBEAT_MS = 10000;
const TAB_DEAD_MS = 35000;
const tabAlive = (tab) => {
  try {
    const at = Number(window.localStorage.getItem(TAB_KEY_PREFIX + tab) || 0);
    return Date.now() - at < TAB_DEAD_MS;
  } catch (_) {
    return false;
  }
};
function readStore(key) {
  try {
    const v = JSON.parse(window.localStorage.getItem(key) || 'null');
    return Array.isArray(v) ? v.filter(x => x && x.op && typeof x.op.op_id === 'string') : [];
  } catch (_) {
    return [];
  }
}
function writeStore(key, list) {
  try {
    if (list.length) window.localStorage.setItem(key, JSON.stringify(list));
    else window.localStorage.removeItem(key);
  } catch (_) {
    // Full, private mode, blocked: the queue still works in memory. What was
    // stored before must not outlive this (it may hold edits since settled or
    // cancelled, which a later open would send again).
    try { window.localStorage.removeItem(key); } catch (__) { /* blocked */ }
  }
}

/** One edit's ops share a gesture id: one Undo takes the whole edit back. */
const mintGesture = () => mintOpId().replace(/^op_/, 'g_');

/**
 * The planner's op queue (trip co-editing Stage C, plan §4.6–4.8): pending
 * ops go to POST /api/trips/:id/ops in order, one batch in flight at a time,
 * and each answer confirms or refuses them.
 *
 * An entry ({ op, replay }, src/utils/tripActivityOps.js) is pending until
 * the planner has read a server copy that includes it: `replayable()` is
 * what useTripState replays onto every re-read. An applied op stays until a
 * read at or after the revision that applied it (`noteServerRead`), so a GET
 * that raced the answer can't make it vanish.
 *
 * The Stage B contract, honoured here:
 *   - a refused op's id is burned (the server logged it: it answers
 *     "duplicate" for good) — a held edit is re-sent under a NEW id;
 *   - reason 'error' was not logged, so the same op may be sent again;
 *   - a duplicate answer repeats what the op did, and counts as its answer.
 *
 * Notices (plan D4), one at a time in the planner's banner slot:
 *   - 'overwrote': your change replaced one you hadn't seen — Keep theirs;
 *   - 'removed':  someone removed a stop you were editing — Restore it with
 *                 your changes / Leave it removed (dismiss = leave it);
 *   - 'failed':   a change couldn't be saved (the planner re-reads);
 *   - 'undo':     what an Undo or Redo did (Stage E): done, done but someone
 *                 else's later change kept, nothing to undo, or still saving;
 *   - 'stale':    edits kept on this device too long to send were dropped (D6).
 *
 * @param {object} args
 * @param {string} args.tripId
 * @param {boolean} args.enabled - ops mode is on for this planner
 * @param {Function} args.getKnownRevision / setKnownRevision - the revision
 *        the planner's copy reflects (useTripState.knownRevisionRef)
 * @param {Function} args.absorb - (entries) the server holds these now and
 *        no read will bring them: fold them into the planner's server copy
 * @param {Function} args.onChanged - the queue changed (re-render the shown days)
 * @param {Function} args.requestReload - re-read the trip
 * @param {Function} args.canSend - (op) => 'send'; 'wait' while it names a
 *        day the server doesn't have yet (a new day still on its way by the
 *        save); 'drop' when that day is gone before it got there
 * @param {Function} args.stopNamed - (key) => { stop, dayUid } as shown now
 * @param {Function} [args.onApplied] - (tripId) an answer applied something
 */
export default function useTripOpsSync({ tripId, userId = null, enabled, isActive, getKnownRevision, setKnownRevision, absorb, onChanged, requestReload, canSend, stopNamed, labelFor, onTripSettingKept, onApplied }) {
  const queueRef = useRef([]); // [{ op, replay, state: 'queued'|'sent'|'confirmed', confirmedAt, tries }]
  const inFlightRef = useRef(false);
  const timerRef = useRef(null);
  // An Undo is under way: edits made meanwhile wait (marked afterUndo), so
  // the undo takes back the change before them and they land on its result.
  const undoingRef = useRef(false);
  const backoffRef = useRef(0);
  const [notices, setNotices] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  // Callbacks read through a ref so the sender never closes over an old render.
  const cb = useRef({});
  cb.current = { isActive, getKnownRevision, setKnownRevision, absorb, onChanged, requestReload, canSend, stopNamed, labelFor, onTripSettingKept, onApplied };
  const tripIdRef = useRef(tripId);
  tripIdRef.current = tripId;
  // One per trip opened: an answer for an earlier visit (A → B → A) is not
  // this one's.
  const sessionRef = useRef(0);

  // Every op id this tab has held: the device copy keeps another tab's
  // entries (same person, same trip) and drops only this tab's settled ones.
  const knownIdsRef = useRef(new Set());
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const [recheck, setRecheck] = useState(0);
  const recheckTimerRef = useRef(null);
  const tabIdRef = useRef(null);
  if (!tabIdRef.current) tabIdRef.current = mintOpId().replace(/^op_/, 'tab_');

  /** Write this tab's unsent entries to the device, per trip. */
  const persist = useCallback(() => {
    const uid = userIdRef.current;
    if (!uid) return;
    const trips = new Set(queueRef.current.map(e => e.tripId));
    trips.add(tripIdRef.current);
    trips.forEach(trip => {
      if (!trip) return;
      const key = storeKey(uid, trip);
      const mine = queueRef.current
        .filter(e => e.tripId === trip && (e.state === 'queued' || e.state === 'sent'))
        .map(e => ({
          op: e.op, replay: e.replay, queuedAt: e.queuedAt || Date.now(), tab: tabIdRef.current,
          ...(e.mayHaveLanded ? { mayHaveLanded: true } : {})
        }));
      const others = readStore(key).filter(x => !knownIdsRef.current.has(x.op.op_id));
      writeStore(key, [...others, ...mine]);
    });
  }, []);

  const counted = useCallback(() => {
    setPendingCount(queueRef.current.filter(e => e.state !== 'confirmed').length);
    persist();
  }, [persist]);

  // This tab is alive (its stored entries are its own while it is).
  useEffect(() => {
    const key = TAB_KEY_PREFIX + tabIdRef.current;
    const beat = () => { try { window.localStorage.setItem(key, String(Date.now())); } catch (_) { /* none */ } };
    const gone = () => { try { window.localStorage.removeItem(key); } catch (_) { /* none */ } };
    // Closing the page runs no unmount: say so on pagehide (and again alive
    // if it comes back from the back/forward cache).
    const onShow = () => beat();
    beat();
    const timer = setInterval(beat, TAB_HEARTBEAT_MS);
    window.addEventListener('pagehide', gone);
    window.addEventListener('pageshow', onShow);
    return () => {
      clearInterval(timer);
      window.removeEventListener('pagehide', gone);
      window.removeEventListener('pageshow', onShow);
      gone();
    };
  }, []);

  // A different trip: nothing pending carries over.
  useEffect(() => {
    sessionRef.current += 1;
    queueRef.current = [];
    inFlightRef.current = false;
    undoingRef.current = false;
    backoffRef.current = 0;
    setNotices([]);
    setPendingCount(0);
  }, [tripId]);

  /**
   * Back on this trip in ops mode: the edits this person left unsent on this
   * device (a closed tab, a phone that lost signal) go on the queue again —
   * shown at once (the planner replays them) and sent; the server applies a
   * repeated op id once. Ones older than OFFLINE_MAX_AGE_MS are dropped, and
   * the person is told.
   */
  useEffect(() => {
    // The planner is on this trip in ops mode now (not the commit that
    // switched trips while the last one's ops mode was still on).
    if (!enabled || !userId || !tripId || (cb.current.isActive && !cb.current.isActive())) return;
    const key = storeKey(userId, tripId);
    const stored = readStore(key);
    if (stored.length === 0) return;
    const now = Date.now();
    // Another open tab's entries are its own to send. One that looks alive
    // may just have closed without a word (a crash, a killed app): look again
    // once its heartbeat would have stopped.
    const adoptable = stored.filter(x => !x.tab || x.tab === tabIdRef.current || !tabAlive(x.tab));
    if (adoptable.length < stored.length) {
      const again = setTimeout(() => setRecheck(n => n + 1), TAB_DEAD_MS + 1000);
      recheckTimerRef.current = again;
    }
    const fresh = adoptable.filter(x => now - (x.queuedAt || 0) <= OFFLINE_MAX_AGE_MS);
    const stale = adoptable.filter(x => now - (x.queuedAt || 0) > OFFLINE_MAX_AGE_MS);
    const have = new Set(queueRef.current.map(e => e.op.op_id));
    let added = 0;
    const settings = {};
    fresh.forEach(x => {
      if (have.has(x.op.op_id)) return;
      knownIdsRef.current.add(x.op.op_id);
      // It may have reached the server already: never merged into.
      queueRef.current.push({ op: x.op, replay: x.replay, state: 'queued', tripId, queuedAt: x.queuedAt, restored: true, everSent: true, mayHaveLanded: !!x.mayHaveLanded });
      if (x.op.type === 'trip.set') Object.assign(settings, x.op.fields || {});
      added++;
    });
    if (stale.length) {
      const goneIds = new Set(stale.map(x => x.op.op_id));
      writeStore(key, stored.filter(x => !goneIds.has(x.op.op_id)));
      // Sent before the planner closed, online — most likely landed: no news.
      const lost = stale.filter(x => !x.mayHaveLanded).length;
      if (lost) setNotices(prev => [...prev, { id: `${Date.now()}_stale`, kind: 'stale', count: lost }]);
    }
    if (added) {
      // The trip's own settings (name, dates…) live in the planner's form:
      // a restored change shows there at once, as its stops do.
      Object.entries(settings).forEach(([field, value]) => cb.current.onTripSettingKept?.(field, value ?? null));
      counted();
      cb.current.onChanged();
      timerRef.current = timerRef.current || setTimeout(() => { timerRef.current = null; flushRef.current(); }, 0);
    }
  }, [enabled, userId, tripId, counted, recheck]);
  useEffect(() => () => { if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current); }, [tripId]);

  // Back online: send what waited, now (not at the end of a backoff).
  useEffect(() => {
    if (!enabled) return undefined;
    const onOnline = () => {
      backoffRef.current = 0;
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      flushRef.current();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [enabled]);

  const notify = useCallback((notice) => {
    setNotices(prev => [...prev, { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, ...notice }]);
  }, []);

  const schedule = useCallback((ms = FLUSH_MS) => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => { timerRef.current = null; flushRef.current(); }, ms);
  }, []);

  const send = useCallback(async ({ keepalive = false, forTrip = null } = {}) => {
    // Leaving (keepalive) sends the trip it was made on whatever this render
    // thinks; otherwise only on the trip the planner is in ops mode on now.
    if (!keepalive && (!enabled || inFlightRef.current || (cb.current.isActive && !cb.current.isActive()))) return;
    // Every entry names the trip it was made on: a flush on the way to
    // another trip sends that trip's ops to it.
    const sentFor = forTrip || tripIdRef.current;
    const queue = queueRef.current;
    // In order. An op naming a day the server hasn't got yet waits, and so
    // does every later op on the same stop (one stop's ops stay in order);
    // other stops' ops go. One whose day is gone is dropped and said.
    const batch = [];
    const waiting = new Set();
    let dropped = false;
    let aged = 0;
    // Kept here too long (an open planner offline for days): as on reopening
    // — and a stop's later ops go with its own (a stop's ops stay in order).
    queue.forEach((e, i) => {
      if (e.state !== 'queued' || e.tripId !== sentFor) return;
      if (Date.now() - (e.queuedAt || Date.now()) <= OFFLINE_MAX_AGE_MS) return;
      e.state = 'dropped';
      if (!e.mayHaveLanded) aged++;
      if (e.op.type.startsWith('activity.')) {
        for (let j = i + 1; j < queue.length; j++) {
          if (queue[j].state === 'queued' && queue[j].op.target === e.op.target) queue[j].state = 'dropped';
        }
      }
    });
    // Days a day.add still on its way creates (queued or in flight): ops
    // naming them go with or after it, in order.
    const comingDays = new Set(queue.filter(x => x.op.type === 'day.add' && x.state !== 'dropped' && x.tripId === sentFor).map(x => x.op.target));
    for (let i = 0; i < queue.length; i++) {
      const e = queue[i];
      if (e.state !== 'queued' || e.tripId !== sentFor) continue;
      if (waiting.has(e.op.target)) continue;
      // Made during an Undo: after it (leaving, it goes anyway).
      if (e.afterUndo && undoingRef.current && !keepalive) { waiting.add(e.op.target); continue; }
      const verdict = sentFor !== tripIdRef.current ? 'send' : cb.current.canSend(e.op, comingDays);
      if (verdict === 'drop') {
        // Its day is gone: so is every later op for that stop (a stop's ops
        // stay in order). Only a stop's: the day and trip ops share targets
        // ('trip') that say nothing about each other.
        e.state = 'dropped';
        if (e.op.type.startsWith('activity.')) {
          for (let j = i + 1; j < queue.length; j++) {
            const x = queue[j];
            if (x.state === 'queued' && x.op.target === e.op.target && !batch.includes(x)) x.state = 'dropped';
          }
          waiting.add(e.op.target);
        }
        dropped = true;
        continue;
      }
      // Leaving, a waiting op goes anyway: the day save is flushed beside it
      // (best effort — Stage F keeps unsent ops on the device).
      if (verdict === 'wait' && !keepalive) { waiting.add(e.op.target); continue; }
      batch.push(e);
      if (batch.length >= 100) break;
    }
    if (dropped || queue.some(x => x.state === 'dropped')) {
      queueRef.current = queueRef.current.filter(x => x.state !== 'dropped');
      counted();
      if (dropped) notify({ kind: 'failed' });
      if (aged) notify({ kind: 'stale', count: aged });
      cb.current.onChanged();
    }
    if (batch.length === 0) return;
    const body = JSON.stringify({ base_revision: cb.current.getKnownRevision(), ops: batch.map(e => e.op), edit_origin: EDIT_ORIGIN });
    // Sent: it may reach the server whatever comes back, so nothing is ever
    // merged into it again (coalescing would keep its id — answered as a
    // duplicate of the older value).
    batch.forEach(e => { e.state = 'sent'; e.everSent = true; });
    if (keepalive) {
      // Leaving: fire and forget; the answer won't be read. If the page
      // comes back (back/forward cache) these go again (pageshow, below).
      // Online, it most likely lands: kept on the device all the same (sent
      // again next time, answered once), but not reported lost if too old.
      const online = typeof navigator === 'undefined' || navigator.onLine !== false;
      batch.forEach(e => { e.unanswered = true; if (online) e.mayHaveLanded = true; });
      persist();
      try {
        fetch(`${API_BASE}/api/trips/${sentFor}/ops`, { method: 'POST', headers: AuthService.getJsonHeaders(), body, keepalive: true }).catch(() => {});
      } catch (_) { /* tearing down */ }
      return;
    }
    const session = sessionRef.current;
    inFlightRef.current = true;
    let answer = null;
    let status = 0;
    try {
      const res = await fetch(`${API_BASE}/api/trips/${sentFor}/ops`, { method: 'POST', headers: AuthService.getJsonHeaders(), body });
      status = res.status;
      answer = await res.json().catch(() => null);
    } catch (_) {
      answer = null;
    }
    if (sessionRef.current !== session) return; // the page moved on (the reset cleared the queue)
    inFlightRef.current = false;

    if (!answer || !answer.success || !Array.isArray(answer.results)) {
      if (status === 403 || status === 404) {
        // No longer this person's to edit: nothing queued can land.
        queueRef.current = queueRef.current.filter(e => e.state === 'confirmed');
        counted();
        notify({ kind: 'failed' });
        cb.current.onChanged();
        cb.current.requestReload();
        return;
      }
      // Unknown whether it landed (network, 5xx): send the same ops again —
      // a repeated op_id is applied once.
      batch.forEach(e => { e.state = 'queued'; });
      backoffRef.current = Math.min(MAX_BACKOFF_MS, backoffRef.current ? backoffRef.current * 2 : 1000);
      schedule(backoffRef.current);
      return;
    }
    backoffRef.current = 0;

    const byId = new Map(answer.results.map(r => [r.op_id, r]));
    const held = new Map(); // target → [entries]
    let reload = !!answer.behind;
    let failed = false;
    batch.forEach(e => {
      const r = byId.get(e.op.op_id);
      // Applied before (a resend, a restored entry): the server holds it, but
      // as it was then — re-read rather than fold it in at this revision.
      // Taken back by an undo since: settled, nothing to say.
      if (r?.status === 'duplicate' && r.original_status === 'undone') {
        e.state = 'dropped';
        reload = true;
        return;
      }
      const status = r?.status === 'duplicate' ? (r.original_status === 'applied' ? 'applied' : 'refused') : r?.status;
      const reason = r?.reason;
      if (status === 'applied') {
        e.state = 'confirmed';
        e.confirmedAt = answer.revision;
        if (r.status === 'duplicate') { e.duplicate = true; reload = true; }
        // A restore this planner had no copy of the stop for: a re-read shows it.
        if (e.replay?.kind === 'none') reload = true;
        const label = e.op.type === 'activity.set' ? labelOf(cb.current.stopNamed(e.op.target)?.stop) : cb.current.labelFor?.(e.op) ?? null;
        // Your own earlier value (another device of yours) is nothing to keep:
        // the server leaves those out; an older server may not.
        const self = userIdRef.current;
        const over = (r.overwrote || []).filter(o => !self || !o.by_user_id || String(o.by_user_id) !== String(self));
        // A day's place is one thing to the person (its name, its places,
        // its pin): one notice, and Keep theirs puts back all of it.
        const placeOver = e.op.type === 'day.set' ? over.filter(o => DAY_PLACE.has(o.field)) : [];
        over.filter(o => !placeOver.includes(o)).forEach(o => notify({
          kind: 'overwrote', key: e.op.target, opType: e.op.type, ourValue: e.op.fields?.[o.field], ...o, label
        }));
        if (placeOver.length) {
          notify({
            kind: 'overwrote', key: e.op.target, opType: 'day.set', field: 'locations',
            by_user_id: placeOver.find(o => o.by_user_id)?.by_user_id ?? null, label,
            ourValue: e.op.fields?.locations ?? e.op.fields?.location ?? null,
            their_value: placeOver.find(o => o.field === 'locations')?.their_value ?? placeOver.find(o => o.field === 'location')?.their_value ?? null,
            theirs: Object.fromEntries(placeOver.map(o => [o.field, o.their_value])),
            ours: Object.fromEntries([...DAY_PLACE].filter(f => f in (e.op.fields || {})).map(f => [f, e.op.fields[f]]))
          });
        }
        return;
      }
      if (status === 'refused' && reason === 'error' && r?.status !== 'duplicate' && (e.tries || 0) + 1 < MAX_ERROR_TRIES) {
        e.tries = (e.tries || 0) + 1;
        e.state = 'queued';
        return;
      }
      // Refused (or no answer for it): its id is burned either way.
      e.state = 'dropped';
      if (reason === 'removed') {
        const list = held.get(e.op.target) || [];
        list.push({ ...e, removedBy: r.removed_by || null });
        held.set(e.op.target, list);
      } else {
        if (reason !== 'day_gone') failed = true;
        reload = true;
      }
    });
    held.forEach((entries, key) => {
      // Edits to it still queued would be refused the same way: they wait
      // with the held ones for the person's answer.
      queueRef.current.forEach(x => {
        if (x.state === 'queued' && x.op.target === key) { entries.push(x); x.state = 'dropped'; }
      });
      const shown = cb.current.stopNamed(key);
      notify({ kind: 'removed', key, removedBy: entries[0].removedBy, label: labelOf(shown?.stop || entries.find(x => x.replay?.stop)?.replay?.stop), held: entries, snapshot: shown });
      reload = true;
    });
    if (failed) notify({ kind: 'failed' });
    if (batch.some(e => e.state === 'confirmed')) cb.current.onApplied?.(sentFor);

    const known = cb.current.getKnownRevision();
    let rebase = batch.some(e => e.state === 'dropped');
    if (Number.isInteger(answer.revision) && Number.isInteger(known) && answer.revision <= known) {
      // A read at or after this answer already landed and holds these ops:
      // stop replaying them (they would cover a co-editor's later value).
      queueRef.current = queueRef.current.filter(x => x.state !== 'dropped' && !(x.state === 'confirmed' && x.confirmedAt <= known));
      rebase = true;
    } else if (!answer.behind && Number.isInteger(answer.revision)) {
      // Nothing else had changed: the planner's copy plus these ops is the
      // server's state at this revision.
      cb.current.setKnownRevision(answer.revision);
      const settled = queueRef.current.filter(x => x.state === 'confirmed' && !x.duplicate && x.confirmedAt <= answer.revision);
      cb.current.absorb(settled);
      queueRef.current = queueRef.current.filter(x => x.state !== 'dropped' && !settled.includes(x));
    } else {
      queueRef.current = queueRef.current.filter(x => x.state !== 'dropped');
    }
    counted();
    // A refused or held op's effect leaves the shown days.
    if (rebase) cb.current.onChanged();
    if (reload) cb.current.requestReload();
    if (queueRef.current.some(x => x.state === 'queued')) schedule();
  }, [enabled, counted, notify, schedule, persist]);

  const flushRef = useRef(send);
  flushRef.current = send;

  /** Queue the entries an edit planned (and send them shortly). */
  const enqueue = useCallback((entries) => {
    // Not gated on `enabled`: the planner decides ops mode (opsModeRef) the
    // moment it switches, and an edit made from an older render's callback
    // after that must still be queued, or it is lost.
    if (!entries || entries.length === 0) return;
    const queue = queueRef.current;
    const tripOf = tripIdRef.current;
    // The entries of one call are one edit (one gesture: one Undo takes it
    // back) — joined to an older edit when they merge into it (a coalesced
    // set, a re-add of a removal already sent), so no Undo takes back half
    // of either.
    let gesture = mintGesture();
    const mine = [];
    const adopt = (g) => {
      if (!g || g === gesture) return;
      gesture = g;
      mine.forEach(x => { x.op = { ...x.op, gesture: g }; });
    };
    const push = (e) => {
      const x = { ...e, op: { ...e.op, gesture }, state: 'queued', tripId: tripOf, queuedAt: Date.now(), ...(undoingRef.current ? { afterUndo: true } : {}) };
      knownIdsRef.current.add(x.op.op_id);
      queue.push(x);
      mine.push(x);
    };
    entries.forEach(entry => {
      const target = entry.op.target;
      // A stop removed and added back by two separate edits goes as an edit.
      if (entry.op.type === 'activity.add') {
        const at = queue.findIndex(x => x.op.type === 'activity.remove' && x.op.target === target && x.state !== 'dropped');
        if (at !== -1) {
          const removal = queue[at];
          // One that may have reached the server (a send whose answer was
          // lost, a restored entry) counts as sent: the re-add restores.
          const sent = removal.state !== 'queued' || !!removal.everSent;
          if (!sent) queue.splice(at, 1);
          else adopt(removal.op.gesture);
          reAddAsEdit(removal, entry, { removalSent: sent }).forEach(push);
          return;
        }
      }
      // A stop whose add never went is removed by cancelling the add and
      // everything after it — its day may never reach the server, and nothing
      // would ever send them. Any other queued edits stay: the custom editor
      // removes and re-adds a stop, and its re-add (reAddAsEdit) diffs
      // against the shown stop, which already carries them.
      if (entry.op.type === 'activity.remove') {
        const unsent = queue.filter(x => x.state === 'queued' && x.op.target === target);
        // Only an add that never left: one that may have landed is removed by op.
        if (unsent.some(x => x.op.type === 'activity.add' && !x.everSent)) {
          unsent.forEach(x => queue.splice(queue.indexOf(x), 1));
          return;
        }
      }
      // A newer change to the same fields of the same thing, while the older
      // one is still unsent (typing a place name re-sets a day per key): one
      // op, the newer value, what the planner had seen before the first.
      if (COALESCING.has(entry.op.type)) {
        let last = null;
        for (let i = queue.length - 1; i >= 0; i--) {
          if (queue[i].op.target === target && queue[i].state !== 'dropped' && queue[i].tripId === tripOf) { last = queue[i]; break; }
        }
        const sameFields = (a, b) => JSON.stringify(Object.keys(a || {}).sort()) === JSON.stringify(Object.keys(b || {}).sort());
        // Only when nothing was queued after it: taking the newer value back
        // to the older op's place would send it ahead of ops it follows
        // (a stay extended onto a day added in between).
        const lastQueued = [...queue].reverse().find(x => x.state === 'queued' && x.tripId === tripOf);
        if (last && last === lastQueued && !last.everSent && last.op.type === entry.op.type
            && (entry.op.type === 'stay.set' || entry.op.type === 'transport.set' || sameFields(last.op.fields, entry.op.fields))) {
          adopt(last.op.gesture);
          last.op = { ...entry.op, op_id: last.op.op_id, seen: last.op.seen || entry.op.seen, gesture: last.op.gesture || gesture };
          last.replay = entry.replay.kind === 'set'
            ? { ...entry.replay, patch: { ...(last.replay.patch || {}), ...entry.replay.patch } }
            : entry.replay;
          return;
        }
      }
      push(entry);
    });
    counted();
    schedule();
  }, [counted, schedule]);

  /** What a re-read replays on top: every op the server copy may not hold yet. */
  const replayable = useCallback(() => queueRef.current.filter(e => e.state !== 'dropped'), []);

  /** A server copy at `revision` was read: ops it already holds stop replaying. */
  const noteServerRead = useCallback((revision) => {
    if (!Number.isInteger(revision)) return;
    const before = queueRef.current.length;
    queueRef.current = queueRef.current.filter(e => !(e.state === 'confirmed' && e.confirmedAt <= revision));
    if (queueRef.current.length !== before) counted();
  }, [counted]);

  const dismiss = useCallback((id) => setNotices(prev => prev.filter(n => n.id !== id)), []);

  /** "Keep Jenny's instead": an op putting her value back (seen = ours). */
  const keepTheirs = useCallback((notice) => {
    const type = notice.opType || 'activity.set';
    const op = notice.theirs
      ? { op_id: mintOpId(), type, target: notice.key, fields: { ...notice.theirs }, seen: { ...notice.ours } }
      : {
        op_id: mintOpId(), type, target: notice.key,
        fields: { [notice.field]: notice.their_value ?? null },
        seen: { [notice.field]: notice.ourValue ?? null }
      };
    const replay = type === 'day.set'
      ? { kind: 'day.set', uid: notice.key, patch: Object.assign({}, ...Object.entries(op.fields).map(([f, v]) => dayPatchFromWire(f, v))) }
      : type === 'trip.set'
        ? { kind: 'trip', fields: op.fields }
        : { kind: 'set', key: notice.key, patch: patchFromWire(notice.field, notice.their_value) };
    enqueue([{ op, replay }]);
    // A trip setting lives in the planner's form: it takes her value too.
    if (type === 'trip.set') cb.current.onTripSettingKept?.(notice.field, notice.their_value ?? null);
    dismiss(notice.id);
    cb.current.onChanged();
  }, [enqueue, dismiss]);

  /**
   * "Restore it with your changes": a restore, then the held edits under new
   * ids (the refused ones are burned).
   */
  const restoreWithChanges = useCallback((notice) => {
    const snap = notice.snapshot;
    const moveDay = [...notice.held].reverse().find(e => e.op.type === 'activity.move' && e.op.fields?.day_uid)?.op.fields.day_uid;
    const dayUid = moveDay || snap?.dayUid;
    const restore = {
      op: { op_id: mintOpId(), type: 'activity.restore', target: notice.key, fields: dayUid ? { day_uid: dayUid } : {} },
      replay: snap?.stop ? { kind: 'add', key: notice.key, dayUid: snap.dayUid, stop: snap.stop } : { kind: 'none', key: notice.key }
    };
    const gesture = mintGesture();
    restore.op.gesture = gesture;
    const again = notice.held.map(e => ({ op: { ...e.op, op_id: mintOpId(), gesture }, replay: e.replay }));
    const held = { queuedAt: Date.now(), ...(undoingRef.current ? { afterUndo: true } : {}) };
    [restore, ...again].forEach(e => knownIdsRef.current.add(e.op.op_id));
    queueRef.current.push({ ...restore, state: 'queued', tripId: tripIdRef.current, ...held }, ...again.map(e => ({ ...e, state: 'queued', tripId: tripIdRef.current, ...held })));
    counted();
    dismiss(notice.id);
    cb.current.onChanged();
    schedule();
  }, [counted, dismiss, schedule]);

  // Leaving the page (or the trip): whatever is queued goes now, to the trip
  // it was made on (captured here: by cleanup time the ref holds the next).
  useEffect(() => {
    if (!enabled) return undefined;
    const thisTrip = tripId;
    const onHide = () => flushRef.current({ keepalive: true, forTrip: thisTrip });
    // Back from the back/forward cache: what went unanswered goes again
    // (same op ids — a repeat is applied once).
    const onShow = (e) => {
      if (!e.persisted) return;
      queueRef.current.forEach(x => { if (x.unanswered && x.state === 'sent') { x.state = 'queued'; x.unanswered = false; } });
      schedule();
    };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('pageshow', onShow);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('pageshow', onShow);
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      flushRef.current({ keepalive: true, forTrip: thisTrip });
    };
  }, [enabled, tripId, schedule]);

  /**
   * Discard (the planner's Discard): the edits not sent yet go — from the
   * queue and from this device — and nothing goes on leaving. Ones already
   * sent are on the server (Undo takes those back).
   */
  const discard = useCallback(() => {
    queueRef.current = queueRef.current.filter(e => e.state !== 'queued');
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    counted();
    cb.current.onChanged();
  }, [counted]);

  /** Try sending now (a new day the queue was waiting on has been saved). */
  const kick = useCallback(() => schedule(0), [schedule]);

  /**
   * Undo (or redo) this person's newest change on the trip (Stage E, plan
   * §4.9): the server takes it back from its log, so it reaches changes made
   * before a reload. What is still queued goes first — the newest change is
   * the one to take back — then the planner re-reads. The one-line result
   * goes in the banner slot.
   */
  const undo = useCallback(async ({ redo = false } = {}) => {
    if (!enabled || undoingRef.current) return;
    undoingRef.current = true;
    const forTrip = tripIdRef.current;
    const session = sessionRef.current;
    const say = (result, byUserId = null) => {
      // One at a time: a new result replaces the last.
      setNotices(prev => [...prev.filter(n => n.kind !== 'undo'), { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, kind: 'undo', redo, result, by_user_id: byUserId }]);
    };
    const pendingBefore = () => queueRef.current.some(e => e.tripId === forTrip && !e.afterUndo && (e.state === 'queued' || e.state === 'sent'));
    try {
      // What was made before the Undo goes first: the newest change is the
      // one to take back. (A pause each round: a failed send has its own
      // backoff, which this mustn't hammer through.)
      const deadline = Date.now() + UNDO_DRAIN_MS;
      while (pendingBefore() && Date.now() < deadline) {
        if (!inFlightRef.current) await flushRef.current();
        await new Promise(r => setTimeout(r, 200));
        if (sessionRef.current !== session) return;
      }
      if (pendingBefore()) { say('pending'); return; }
      // One request id for both tries: a retry after a lost answer is
      // answered, not applied again (it would take back a second change).
      const opId = mintOpId();
      const post = async () => {
        const ctl = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = ctl ? setTimeout(() => ctl.abort(), UNDO_TIMEOUT_MS) : null;
        try {
          const res = await fetch(`${API_BASE}/api/trips/${forTrip}/ops/undo`, {
            method: 'POST', headers: AuthService.getJsonHeaders(), ...(ctl ? { signal: ctl.signal } : {}),
            body: JSON.stringify({ op_id: opId, redo, edit_origin: EDIT_ORIGIN })
          });
          return await res.json().catch(() => null);
        } catch (_) {
          return null;
        } finally {
          if (timer) clearTimeout(timer);
        }
      };
      let answer = await post();
      if (!answer) answer = await post();
      if (answer && answer.success && answer.applied > 0) cb.current.onApplied?.(forTrip);
      if (sessionRef.current !== session) return;
      if (!answer || !answer.success) {
        // It may have landed all the same: show what the server has.
        say('failed');
        await cb.current.requestReload();
        return;
      }
      if (!answer.kind) { say('nothing'); return; }
      const kept = answer.kept || [];
      if (answer.applied > 0) say(kept.length ? 'partly' : 'done', answer.kept_by_user_id || null);
      else if (kept.some(k => k.reason === 'changed')) say('kept', answer.kept_by_user_id || null);
      else if (kept.length) say('failed');
      else say('nothing');
      // The edits made meanwhile land on what the undo left.
      if (answer.applied > 0) await cb.current.requestReload();
    } finally {
      if (sessionRef.current === session) {
        undoingRef.current = false;
        let held = false;
        queueRef.current.forEach(e => { if (e.afterUndo) { delete e.afterUndo; held = true; } });
        if (held) schedule();
      }
    }
  }, [enabled, schedule]);

  return { enqueue, replayable, noteServerRead, notices, dismiss, keepTheirs, restoreWithChanges, pendingCount, kick, undo, discard };
}

function labelOf(stop) {
  if (!stop) return null;
  return stop.title || stop.name || stop.tourName || null;
}
